import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { fromDateRange, isAfterAppToday, toDayjsRange } from '@/lib/date';

import {
  dealerDeviceHasActiveFilters,
  dealerDeviceToDefaults,
  dealerDeviceToFilters,
} from '../device-filters';
import { useDealerDeviceFilterOptions } from '../hooks';
import { dealerDeviceFilterFormSchema } from '../schemas';
import type {
  DealerDeviceAllocation,
  DealerDeviceFilterFormValues,
  DealerDeviceFilters,
} from '../types';

import styles from './dealer-device-filter-drawer.module.scss';

export type DealerDeviceFilterDrawerProps = {
  open: boolean;
  dealerId: string;
  allocation: DealerDeviceAllocation;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: DealerDeviceFilters;
  onApply: (filters: DealerDeviceFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<DealerDeviceFilterDrawerProps, 'open'>;

function FilterBody({ dealerId, allocation, value, onApply, onClose }: BodyProps) {
  const form = useForm<DealerDeviceFilterFormValues>({
    resolver: zodResolver(dealerDeviceFilterFormSchema),
    defaultValues: dealerDeviceToDefaults(value),
    mode: 'onChange',
  });
  const [category, deviceType, deviceStatus, subscriptionStatus, dateFrom, dateTo] = useWatch({
    control: form.control,
    name: ['category', 'deviceType', 'deviceStatus', 'subscriptionStatus', 'dateFrom', 'dateTo'],
  });
  const nothingSelected = !dealerDeviceHasActiveFilters(
    dealerDeviceToFilters({
      category,
      deviceType,
      deviceStatus,
      subscriptionStatus,
      dateFrom,
      dateTo,
    }),
  );

  const options = useDealerDeviceFilterOptions(dealerId, allocation);
  const categoryOptions = useMemo(
    () => (options.data?.category ?? []).map((c) => ({ value: c, label: c })),
    [options.data],
  );

  const handleApply = form.handleSubmit((v) => {
    onApply(dealerDeviceToFilters(v));
    onClose();
  });

  // AC order — Figma draws Device Status first; the AC wins on content (grilling Q4).
  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={handleApply}
      // AC: "Apply is disabled while nothing is selected" — but not while filters are applied, or
      // deselecting everything could never clear them (the panel has no Clear Filter).
      primaryDisabled={nothingSelected && !dealerDeviceHasActiveFilters(value)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="category"
        control={form.control}
        label="Category"
        placeholder="Select"
        showSearch
        loading={options.isLoading}
        options={categoryOptions}
      />
      <MultiSelectField
        name="deviceType"
        control={form.control}
        label="Device Type"
        placeholder="Select"
        showSearch
        loading={options.isLoading}
        options={options.data?.deviceType ?? []}
      />
      <MultiSelectField
        name="deviceStatus"
        control={form.control}
        label="Device Status"
        placeholder="Select"
        showSearch
        loading={options.isLoading}
        options={options.data?.deviceStatus ?? []}
      />
      {/* Self Assigned / Device Sold only: `meta/filters` returns these two for those tabs. */}
      {options.data?.subscriptionStatus ? (
        <MultiSelectField
          name="subscriptionStatus"
          control={form.control}
          label="Subscription Status"
          placeholder="Select"
          showSearch
          loading={options.isLoading}
          options={options.data.subscriptionStatus}
        />
      ) : null}
      {options.data?.dateRange ? (
        // House date-range control (warranty/order drawers). The label is the server's own; no
        // "(ET)" suffix because the allocation date is a plain date column. antd's range picker
        // cannot produce To < From, so the schema needs no ordering refinement.
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>{options.data.dateRange.label}</legend>
          <DateRangePicker
            aria-label={options.data.dateRange.label}
            format="MM/DD/YYYY"
            disabledDate={isAfterAppToday}
            style={{ width: '100%' }}
            value={toDayjsRange(dateFrom && dateTo ? [dateFrom, dateTo] : null)}
            onChange={(range) => {
              const iso = fromDateRange(range);
              form.setValue('dateFrom', iso?.[0] ?? null);
              form.setValue('dateTo', iso?.[1] ?? null);
            }}
          />
        </fieldset>
      ) : null}
    </Drawer>
  );
}

export function DealerDeviceFilterDrawer({ open, ...rest }: DealerDeviceFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open, so Cancel discards the draft.
  return <FilterBody {...rest} />;
}
